import { ECharts, EChartsOption } from 'echarts';
import { IEChartsDashboardItem } from 'src/app/components/echarts-dashboard-item/echarts-dashboard-item.component';
import { IEChartsDashboardItemPresentationOptions } from 'src/app/components/echarts-dashboard-item/echarts-dashboard-item-persistent-options.model';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import type {
    FlowChartViewModel,
    NetChartViewModel,
} from './flow-chart.models';
import {
    DashboardItemComponentType,
    DashboardItemFactory,
    IDashboardItemFactoryOptions,
} from 'src/app/utils/dashboard-item';
import { composeBaseDataset } from 'src/app/utils/echarts/echarts';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';

export interface FlowChartDashboardItemFactoryOptions extends IDashboardItemFactoryOptions {
    createdInstanceId: string;
    dashboardName: string;
    title: string | ((context: IQueryContext) => string);
    /** HTML for the card's documentation (info-circle) icon. */
    chartDocumentation?: string;
    /** 'flow' = two-sided stacked bars (inflow ▲ up / outflow ▼ down); 'net' = single signed bar. */
    variant: 'flow' | 'net';
    /** Receives the live ECharts instance from `composeData` (used to wire zoom/legend; no `@Output`). */
    onChartInstance?: (instance: ECharts) => void;
}

const NET_POS_COLOR = ChartColorService.POSITIVE_COLOR;
const NET_NEG_COLOR = ChartColorService.NEGATIVE_COLOR;
const FLOW_TOOLBOX = {
    feature: {
        export: {},
        toggleTableView: {},
        toggleSeriesType: {},
        toggleStackingMode: {},
    },
};
const NET_TOOLBOX = { feature: { export: {}, toggleTableView: {} } };
const FLOW_DEFAULTS: IEChartsDashboardItemPresentationOptions = {
    echartsSeriesType: 'bar',
    stackingMode: 'stacked',
};
const NET_DIM = 'Net';

/**
 * Reusable "flow" chart type. Rides the stock `app-echarts-dashboard-item` and only builds
 * the ECharts option from a view-model ({@link FlowChartViewModel} / {@link NetChartViewModel}) produced by the
 * dashboard's state service.
 */
export class FlowChartDashboardItemFactory extends DashboardItemFactory<
    IEChartsDashboardItem,
    FlowChartDashboardItemFactoryOptions
> {
    override FACTORY_CLASS = 'FlowChartDashboardItemFactory';
    override COMPONENT_CLASS: DashboardItemComponentType =
        'EchartsDashboardItemComponent';

    override getInstance(
        options: FlowChartDashboardItemFactoryOptions
    ): IEChartsDashboardItem {
        const isFlow = options.variant === 'flow';
        return {
            dashboardName: options.dashboardName,
            instanceId: options.createdInstanceId,
            dashboardItem: {
                title: options.title,
                chartDocumentation: options.chartDocumentation,
            },
            title: options.title,
            chartDocumentation: options.chartDocumentation,
            component: this.COMPONENT_CLASS,
            includeInCommonLegend: false,
            toolbox: isFlow ? FLOW_TOOLBOX : NET_TOOLBOX,
            initFactoryOptions: FLOW_DEFAULTS,
            composeData: (
                data: any,
                _ctx?: IQueryContext,
                instance?: ECharts,
                factoryOptions?: IEChartsDashboardItemPresentationOptions
            ) => {
                // live instance from the shared component → state service (see options.onChartInstance).
                if (instance) {
                    options.onChartInstance?.(instance);
                }
                return isFlow
                    ? FlowChartDashboardItemFactory.buildFlowOption(
                          data as FlowChartViewModel,
                          factoryOptions
                      )
                    : FlowChartDashboardItemFactory.buildNetOption(
                          data as NetChartViewModel
                      );
            },
        };
    }

    /**
     * Two-sided flow chart. Series type (bar/line) and stacking (stacked/none) come from the item's
     * menu toggles via `factoryOptions` (effectiveOptions): stacking is applied here from `stackingMode`,
     * while the series type is finalised by the shared component's `changeSeriesType`. We therefore set
     * line-friendly props (no symbols, coloured line) unconditionally — harmless for bars — so a
     * bar→line switch renders clean lines. Inflow positive, outflow negated onto the same axis.
     *
     * Values live in a `dataset` (one row per category, one dimension per series) rather than in
     * `series[].data`, so the shared data export / table view can read the chart straight from the
     * option. Dimension names are the series names, which is what the export uses as headers.
     */
    static buildFlowOption(
        viewModel: FlowChartViewModel,
        factoryOptions?: IEChartsDashboardItemPresentationOptions
    ): EChartsOption {
        const { dates, series, selected, zoomStart, zoomEnd, wheelZoom } =
            viewModel;
        const xKey = viewModel.xAxisKey ?? 'date';
        const isStacked =
            (factoryOptions?.stackingMode ?? 'stacked') === 'stacked';
        const signed = series.map((s) =>
            s.dir === 'in' ? s.data : s.data.map((v) => -(v || 0))
        );
        const source = dates.map((category, index) => {
            const row: Record<string, string | number | null> = {
                [xKey]: category,
            };
            series.forEach((s, seriesIndex) => {
                row[s.name] = signed[seriesIndex][index] ?? null;
            });
            return row;
        });

        // outflow z-order: stacked bars closer to zero drawn on top (descending z).
        let outZ = 0;
        const echSeries = series.map((s) => {
            const seriesOption: any = {
                name: s.name,
                type: factoryOptions?.echartsSeriesType ?? 'bar',
                // Stacking toggle (like the other dashboards): 'stacked' piles the series onto the
                // two-sided flow stack, 'none' draws each series on its own.
                stack: isStacked ? 'flow' : undefined,
                itemStyle: { color: s.color },
                lineStyle: { color: s.color },
                showSymbol: false,
                // no emphasis: legend hover must not lift a series above the stack
                emphasis: { disabled: true },
                encode: { x: xKey, y: s.name },
            };
            // z-order only matters for stacked bars.
            if (isStacked && s.dir === 'out') {
                seriesOption.z = 20 - outZ++;
            }
            return seriesOption;
        });

        return {
            ...composeBaseDataset(source),
            // BiViz standard: no animation.
            animation: false,
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                // confine tooltip to the chart box
                confine: true,
                formatter: (ps: any) => {
                    // dataset rows: the series' value sits under its own dimension (= series name)
                    const valueOf = (p: any) => p.value?.[p.seriesName];
                    // keep series order (▲ in, then ▼ out) — no magnitude sort, matches the legend
                    const points = ps.filter((p: any) => valueOf(p));
                    let s = `<b>${ps[0]?.axisValue}</b><br/>`;
                    for (const p of points.slice(0, 20)) {
                        s += `${p.marker} ${
                            p.seriesName
                        }: <b>${EChartsTooltipHelper.tooltipNumberValueFormatter(
                            valueOf(p)
                        )}</b><br/>`;
                    }
                    return s;
                },
            },
            // scroll legend above the chart
            legend: {
                type: 'scroll',
                orient: 'horizontal',
                top: 8,
                left: 'center',
                width: '92%',
                pageButtonPosition: 'end',
                selected,
            },
            grid: {
                top: 48,
                left: 8,
                right: 24,
                bottom: 44,
                containLabel: true,
            },
            xAxis: { type: 'category' },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
            },
            dataZoom: [
                {
                    type: 'slider',
                    start: zoomStart,
                    end: zoomEnd,
                    bottom: 8,
                    height: 18,
                    showDetail: false,
                },
                // Wheel/drag zoom: include the `inside` component only when enabled. When off it is
                // omitted entirely (not merely zoomOnMouseWheel:false) so ECharts does not capture the
                // mouse wheel — the page scrolls normally while the cursor is over the chart.
                ...((wheelZoom ?? true)
                    ? [
                          {
                              type: 'inside' as const,
                              start: zoomStart,
                              end: zoomEnd,
                          },
                      ]
                    : []),
            ],
            series: echSeries,
        };
    }

    /**
     * Net balance: a single bar series, green ≥ 0 / red < 0, with a zero mark line.
     */
    static buildNetOption(viewModel: NetChartViewModel): EChartsOption {
        const { dates, data, zoomStart, zoomEnd, wheelZoom } = viewModel;
        const xKey = viewModel.xAxisKey ?? 'date';
        const source = dates.map((category, index) => ({
            [xKey]: category,
            [NET_DIM]: data[index] ?? null,
        }));
        const netValueOf = (p: any) => p.value?.[NET_DIM] ?? 0;
        return {
            animation: false,
            ...composeBaseDataset(source),
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                confine: true,
                formatter: (ps: any) =>
                    `<b>${ps[0]?.axisValue}</b><br/>${
                        ps[0]?.marker
                    } Net: <b>${EChartsTooltipHelper.tooltipNumberValueFormatter(
                        netValueOf(ps[0])
                    )}</b>`,
            },
            grid: {
                top: 12,
                left: 8,
                right: 24,
                bottom: 28,
                containLabel: true,
            },
            xAxis: { type: 'category' },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
            },
            dataZoom: [
                { type: 'slider', start: zoomStart, end: zoomEnd, show: false },
                ...((wheelZoom ?? true)
                    ? [
                          {
                              type: 'inside' as const,
                              start: zoomStart,
                              end: zoomEnd,
                          },
                      ]
                    : []),
            ],
            series: [
                {
                    name: NET_DIM,
                    type: 'bar',
                    encode: { x: xKey, y: NET_DIM },
                    itemStyle: {
                        color: (p: any) =>
                            netValueOf(p) >= 0 ? NET_POS_COLOR : NET_NEG_COLOR,
                    },
                    markLine: {
                        silent: true,
                        symbol: 'none',
                        data: [{ yAxis: 0 }],
                        lineStyle: { color: '#bbb' },
                    },
                },
            ],
        };
    }
}
